import { cloneDeep as _cloneDeep, mergeWith as _mergeWith } from 'lodash';
import type { ApplicationDebugDto } from '@/types/forms/specific/applicationForm';
import { debugDto } from '@/debug/form/application';

export type ApplicationDebugPresetId =
    | 'pv-low-voltage'
    | 'pv-medium-voltage'
    | 'storage-low-voltage'
    | 'storage-medium-voltage'
    | 'wind-low-voltage'
    | 'wind-medium-voltage'
    | 'chp-high-efficiency'
    | 'chp-non-high-efficiency';

export interface ApplicationDebugPreset {
    id: ApplicationDebugPresetId;
    label: string;
    dto: ApplicationDebugDto;
}

const repeat = <T>(value: T, count: number): T[] =>
    Array.from({ length: count }, () => _cloneDeep(value));

const first = <T>(values: T[] | undefined): T => {
    if (!values?.[0]) {
        throw new Error('Debug preset source data is incomplete.');
    }
    return values[0];
};

const createPreset = (
    id: ApplicationDebugPresetId,
    label: string,
    overrides: ApplicationDebugDto,
): ApplicationDebugPreset => ({
    id,
    label,
    dto: _mergeWith(
        _cloneDeep(debugDto),
        overrides,
        (_targetValue, sourceValue) =>
            Array.isArray(sourceValue) ? sourceValue : undefined,
    ),
});

const pvModule = first(debugDto.plantPerformance?.photovoltaic?.pvModules);
const inverterModule = first(
    debugDto.plantPerformance?.inverter?.inverterModules,
);
const storageModule = first(debugDto.plantPerformance?.storage?.storageModules);
const generatorModule = first(
    debugDto.plantPerformance?.generator?.generatorModules,
);

const photovoltaic = (count: number): ApplicationDebugDto => ({
    generationType: {
        energyType: 'sun',
        plantType: 'photovoltaicPowerPlant',
        plantStructureType: 'building',
        totalPlantPerformance: 'below300',
        requestType: 'requestWithoutPlanning',
        plugInGeneratingPlant: false,
    },
    plantPerformance: {
        photovoltaic: { pvModules: repeat(pvModule, count) },
        inverter: { inverterModules: repeat(inverterModule, count) },
    },
});

const storage = (count: number): ApplicationDebugDto => ({
    generationType: {
        energyType: 'energyStorage',
        plantType: 'storage',
        totalPlantPerformance: 'below300',
        requestType: 'requestWithoutPlanning',
    },
    plantPerformance: {
        inverter: { inverterModules: repeat(inverterModule, count) },
        storage: { storageModules: repeat(storageModule, count) },
    },
});

const wind = (count: number): ApplicationDebugDto => ({
    generationType: {
        energyType: 'wind',
        plantType: 'windPowerPlant',
        totalPlantPerformance: 'below300',
        requestType: 'requestWithoutPlanning',
    },
    plantPerformance: {
        generator: { generatorModules: repeat(generatorModule, count) },
    },
});

const chp = (
    powerHeatCouplingPlantType:
        | 'highEfficiencyChpPlant'
        | 'nonHighEfficiencyChpPlant',
): ApplicationDebugDto => ({
    generationType: {
        energyType: 'naturalGas',
        plantType: 'thermalPowerPlantKwk',
        totalPlantPerformance: 'below300',
        requestType: 'requestWithoutPlanning',
    },
    plantData: { powerHeatCouplingPlantType },
    plantPerformance: {
        generator: { generatorModules: repeat(generatorModule, 1) },
    },
});

const powerConnection = (
    whichVoltageLevel: 'lowVoltage' | 'mediumVoltage',
) => ({
    doesConnectionExist: 'yes' as const,
    whichVoltageLevel,
});

export const applicationDebugPresets: ApplicationDebugPreset[] = [
    createPreset('pv-low-voltage', 'PV NS - 2 Wechselrichter', {
        ...photovoltaic(2),
        powerConnection: powerConnection('lowVoltage'),
    }),
    createPreset('pv-medium-voltage', 'PV MS - 4 Wechselrichter', {
        ...photovoltaic(4),
        powerConnection: powerConnection('mediumVoltage'),
    }),
    createPreset('storage-low-voltage', 'Speicher NS - 2 Speicher', {
        ...storage(2),
        powerConnection: powerConnection('lowVoltage'),
    }),
    createPreset('storage-medium-voltage', 'Speicher MS - 2 Speicher', {
        ...storage(2),
        powerConnection: powerConnection('mediumVoltage'),
    }),
    createPreset('wind-low-voltage', 'Wind NS - 2 Generatoren', {
        ...wind(2),
        powerConnection: powerConnection('lowVoltage'),
    }),
    createPreset('wind-medium-voltage', 'Wind MS - 2 Generatoren', {
        ...wind(2),
        powerConnection: powerConnection('mediumVoltage'),
    }),
    createPreset('chp-high-efficiency', 'KWK hocheffizient', {
        ...chp('highEfficiencyChpPlant'),
        powerConnection: powerConnection('lowVoltage'),
    }),
    createPreset('chp-non-high-efficiency', 'KWK nicht hocheffizient', {
        ...chp('nonHighEfficiencyChpPlant'),
        powerConnection: powerConnection('lowVoltage'),
    }),
];

export const getApplicationDebugPreset = (
    id: ApplicationDebugPresetId,
): ApplicationDebugPreset | undefined =>
    applicationDebugPresets.find((preset) => preset.id === id);
